<template>
  <div class="tw-flex tw-flex-col tw-h-full">
    <FancyServerTable
      v-if="isStateLoaded"
      ref="fancyTableRef"
      :data="projectTableData"
      :columns="projectTableHeaders"
      :enable-filters="true"
      :enable-pagination="true"
      :enable-column-visibility="true"
      :enable-column-pinning="true"
      :initial-column-visibility="currentSavedState?.columnVisibility || {}"
      :initial-column-order="currentSavedState?.columnOrder || []"
      :initial-column-sizing="currentSavedState?.columnSizing || {}"
      :initial-column-filters="currentSavedState?.columnFilters || []"
      :initial-sorting="currentSavedState?.sorting ?? []"
      :initial-column-pinning="currentSavedState?.columnPinning || { left: [], right: ['actions'] }"
      :total-rows="totalRows"
      :page-index="pageIndex"
      :page-size="pageSize"
      :auto-filter="true"
      enable-row-selection
      :get-row-id="row => String(row.id)"
      @update:row-selection="handleRowSelectionChange"
      :loading="isLoading"
      @update:state="debouncedHandleTableStateChange"
      @fetch-data="fetchProjects"
    >
      <template #config-manager>
        <FancyTableConfigManager
          :saved-configs="savedTableConfigs"
          :current-config="currentSavedState"
          :is-loading="false"
          :can-save-state="canSaveState"
          @save-config="handleSaveConfig"
          @update-config="handleUpdateConfig"
          @apply-config="handleApplyConfig"
          @delete-config="handleDeleteConfig"
          @mark-as-default="handleMarkAsDefault"
        />
      </template>
      <template #actions>
        <ProjectsTableBulkActions
          :selectedProjects="selectedProjects"
          v-if="selectedProjects.length > 1"
          @reloadTable="getProjectsTableList"
        />
      </template>
    </FancyServerTable>
  </div>
</template>

<script setup lang="ts">
import { h, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useProjectStore } from '@/stores/project.store';
import {
  FancyServerTable,
  type FancyTableColumnDef,
  type ServerTableOptions,
  useTableRenderers,
  FieldType,
  FilterType,
  FancyTableConfigManager,
} from '@/components/shared/fancyTables';

import ProjectsTableRowActions from './ProjectsTableRowActions.vue';
import ProjectsTableBulkActions from './ProjectsTableBulkActions.vue';
import { useFancyTableConfig } from '@/_helpers/composables/fancy-table-config.composable';

const selectedProjects = ref<any[]>([]);
const fancyTableRef = ref<any>(null);
const { GetFieldRenderer } = useTableRenderers();
const isLoading = ref(false);

const { getProjects } = useProjectStore();
const { t } = useI18n();

const props = defineProps<{
  customerId: number;
}>();

const projectTableData = ref<any[]>([]);
const totalRows = ref(0);
const pageIndex = ref(0);
const pageSize = ref(10);

// Map filters to API params
const mapFiltersToApiParams = (filters: any[]) => {
  const params: any = {};

  filters.forEach(filter => {
    const { id, value } = filter;

    switch (id) {
      case 'name':
        if (value) params.name = value;
        break;
      case 'project_limit':
        if (value?.start !== undefined) params.project_limit_min = value.start;
        if (value?.end !== undefined) params.project_limit_max = value.end;
        break;
      case 'auto_approval_limit':
        if (value?.start !== undefined) params.auto_approval_limit_min = value.start;
        if (value?.end !== undefined) params.auto_approval_limit_max = value.end;
        break;
      case 'project_limit_frequency':
        if (value && value.length > 0) params.project_limit_frequency = value.join(',');
        break;
      default:
        break;
    }
  });

  return params;
};

// Map sorting to API param
const mapSortingToApiParam = (sorting: any[]): string => {
  if (sorting.length === 0) return '';

  const sort = sorting[0];
  const { id, desc } = sort;
  let backendField = id;

  switch (id) {
    case 'name':
      backendField = 'name';
      break;
    case 'project_limit':
      backendField = 'project_limit_sort';
      break;
    case 'auto_approval_limit':
      backendField = 'auto_approval_limit_sort';
      break;
    case 'project_limit_frequency':
      backendField = 'project_limit_frequency';
      break;
    default:
      backendField = id;
  }

  return desc ? `-${backendField}` : backendField;
};

// Server-side fetch function
const fetchProjects = async (options?: ServerTableOptions) => {
  isLoading.value = true;

  try {
    const currentPageIndex = options?.pageIndex ?? 0;
    const currentPageSize = options?.pageSize ?? 10;
    const filters = options?.filters ?? [];
    const sorting = options?.sorting ?? [];

    // Update pagination state
    pageIndex.value = currentPageIndex;
    pageSize.value = currentPageSize;

    // Map filters to API params
    const filterParams = mapFiltersToApiParams(filters);

    // Map sorting to API param
    const orderingParam = mapSortingToApiParam(sorting);

    const apiParams = {
      page: currentPageIndex + 1,
      page_size: currentPageSize,
      customer: props.customerId,
      is_active: true,
      ...filterParams,
      ...(orderingParam && { ordering: orderingParam }),
    };

    const response = await getProjects(apiParams, '');

    projectTableData.value = response.results;
    totalRows.value = response.count;
  } catch (error) {
    console.error('Error fetching projects:', error);
    projectTableData.value = [];
    totalRows.value = 0;
  } finally {
    isLoading.value = false;
  }
};

const getProjectsTableList = async () => {
  // This is now a wrapper for fetchProjects for backward compatibility
  // Reset selection after data reload to clear deleted rows from selection
  selectedProjects.value = [];

  // Reset the table's internal row selection state
  if (fancyTableRef.value) {
    fancyTableRef.value.resetRowSelection();
  }

  await fetchProjects();
};

const {
  savedTableConfigs,
  currentSavedState,
  canSaveState,
  isStateLoaded,
  handleSaveConfig,
  handleUpdateConfig,
  handleApplyConfig,
  handleDeleteConfig,
  handleMarkAsDefault,
  debouncedHandleTableStateChange,
} = useFancyTableConfig({
  tableName: 'projects',
  fancyTableRef: fancyTableRef,
  tableLoader: getProjectsTableList,
});

const frequencyOptions = [
  {
    label: t('customer_management.main_data.frequency_weekly'),
    value: 'WEEKLY',
  },
  {
    label: t('customer_management.main_data.frequency_monthly'),
    value: 'MONTHLY',
  },
  {
    label: t('customer_management.main_data.frequency_yearly'),
    value: 'YEARLY',
  },
  {
    label: t('customer_management.main_data.frequency_all_time'),
    value: 'ALL_TIME',
  },
];

const projectTableHeaders: FancyTableColumnDef<any>[] = [
  {
    header: t('customer_management.titles.name'),
    accessorKey: 'name',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 250,
  },
  {
    header: t('customer_management.forms.project_limit'),
    accessorKey: 'project_limit',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.MONEY,
    filterType: FilterType.NUMBER_RANGE,
    filterFn: 'inNumberRange',
    sortingFn: 'money',
    cell: GetFieldRenderer(FieldType.MONEY),
    size: 200,
  },
  {
    header: t('customer_management.assign_team.auto_approval_limit_amount'),
    enableSorting: true,
    accessorKey: 'auto_approval_limit',
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.MONEY,
    filterType: FilterType.NUMBER_RANGE,
    filterFn: 'inNumberRange',
    sortingFn: 'money',
    cell: GetFieldRenderer(FieldType.MONEY),
    size: 200,
  },
  {
    header: t('customer_management.titles.project_limit_frequency'),
    accessorKey: 'project_limit_frequency',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    filterType: FilterType.MULTI_SELECT,
    filterFn: 'enumFilter',
    cell: GetFieldRenderer(FieldType.ENUM, {
      enumOptions: frequencyOptions,
    }),
    filterOptions: frequencyOptions,
    size: 150,
  },
  {
    header: t('common.headers.actions'),
    accessorKey: 'actions',
    enableSorting: false,
    enableColumnFilter: false,
    enableHiding: false,
    hideFromColumnConfig: true,
    cell: info => {
      return h(ProjectsTableRowActions, {
        key: `actions-${info.row.original.id}`,
        project: info.row.original,
        onReloadTable: getProjectsTableList,
      });
    },
  },
];

const handleRowSelectionChange = (selection: any[]) => {
  selectedProjects.value = selection;
};

onMounted(async () => {
  await getProjectsTableList();
});
</script>
